/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { NumericFormat } from 'react-number-format';
import Styles from '../../css/LoanInformation.module.css';
import EnvelopeWithBox from '../EnvelopeWithBox';
import { useLoanInformationContext } from '../../contexts/LoanInformationContext';
import {
  addMonthsToDate,
  convertDateInputTo8AMLocal,
  dateToEpoch,
  formatDateForInputWithOffset,
  getTimestampAsDate,
  getLpiDueDateString,
} from '../../common/UtilityFunctions';
import useFeeList from '../../common/hooks/useFeeList';
import useQidList from '../../common/hooks/useQidList';
import { useMemo } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoanContext } from '../../contexts/LoanContext';
import useGetServicingInstallments from '../../common/hooks/useGetServicingInstallments';

const LoanDetails = () => {
  const {
    lnptData,
    servicingData,
    propData,
    handlePropChange,
    loanData,
    handleLoanChange,
    handleServicingChange,
    loanEvents,
    handleLoanEventChange,
    originalLoanData,
  } = useLoanInformationContext();

  const { fullAccessSuperUser } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const dueDateString = getLpiDueDateString(loanData?.lpi);
  const { servInstData: currentInstallmentsInfo } = useGetServicingInstallments(loanNumber, dueDateString);
  const { totalDiscount, borrowerDiscount } = useFeeList();

  const flatLateChargeValue = useMemo(() => {
    if (!loanData) return 0;
    if (!loanData.lteChrg && !loanData.lateChrgMin) return 0;
    if (loanData.lteChrg && loanData.lteChrg != 0) return loanData.lteChrg;
    if (loanData.lteChrg == 0) return loanData.lateChrgMin;
  }, [loanData]);

  const lateChargeType = useMemo(() => {
    if (!loanData) {
      return '';
    } else {
      return loanData?.lateChrgType === true ? 'true' : 'false';
    }
  }, [loanData]);

  const calculatedLateCharge = useMemo(() => {
    if (!loanData?.lteChrg) return 0;
    const pi = currentInstallmentsInfo?.pi || 0;
    const ti = currentInstallmentsInfo?.ti || 0;
    const base = loanData?.lateChrgType === true ? pi : pi + ti;
    return (loanData.lteChrg / 100) * base;
  }, [loanData?.lteChrg, loanData?.lateChrgType, currentInstallmentsInfo?.pi, currentInstallmentsInfo?.ti]);

  const { qid: propTypeQid } = useQidList('MSC4_CD.010');
  const { qid: floodZoneQid } = useQidList('PRP1_CD.143');
  const { qid: loanPurposeQid } = useQidList('TAB8_CD.031');
  const { qid: servicingLoanTypeQid } = useQidList('MASZ_CD.060');

  return (
    <EnvelopeWithBox blockName='Loan Details'>
      <div className={Styles.loanInfoTable}>
        <div className={Styles.leftColumn}>
          <table className={Styles.loanInfoTableSection}>
            <tbody>
              <tr>
                <th>Original Loan Amount:</th>
                <td>
                  <NumericFormat
                    value={loanData?.origLoanBalance ? loanData?.origLoanBalance : ''}
                    decimalScale={2}
                    fixedDecimalScale
                    disabled={false}
                    prefix='$'
                    thousandSeparator
                    displayType='text'
                  />
                </td>
              </tr>

              <tr>
                <th>Origination Type:</th>
                <td>{loanData?.originationType ? loanData?.originationType : ''}</td>
              </tr>
              <tr>
                <th>Lien Position</th>
                <td>{loanData?.lienPosition ?? ''}</td>
              </tr>
              <tr>
                <th>Servicing Loan Type:</th>
                <td>
                  <select
                    value={servicingData?.servicingLoanType ?? ''}
                    onChange={(e) => handleServicingChange('edit', 'servicingLoanType', e.target.value)}
                  >
                    <option value='' disabled>
                      -- Select an Option --
                    </option>

                    {servicingLoanTypeQid.map((option, index) => (
                      <option key={index} value={option.code}>
                        {option.code}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
              <tr>
                <th>Servicing Loan Type Description:</th>
                <td>
                  {servicingData?.servicingLoanTypeDescription ? servicingData?.servicingLoanTypeDescription : ''}
                </td>
              </tr>
              <tr>
                <th>Property Type:</th>
                <td>
                  <select
                    name={'Property Type'}
                    value={propData?.propertyType ? propData.propertyType : ''}
                    onChange={(e) => handlePropChange('edit', 'propertyType', e.target.value)}
                  >
                    <option value='' disabled>
                      -- Select an Option --
                    </option>
                    {propTypeQid.map((opt, i) => (
                      <option key={i} value={opt.code}>
                        {opt.code} - {opt.description}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
              <tr>
                <th>Servicer Rate:</th>
                <td>
                  <input
                    type='number'
                    name='zServFeePct'
                    value={String(servicingData?.servFeePct || '')}
                    placeholder='Servicer Rate'
                    onChange={(e) => {
                      let val = parseFloat(e.target.value);
                      if (isNaN(val)) return;
                      handleServicingChange('edit', 'servFeePct', val);
                      handleServicingChange('edit', 'servFeeAmt', 0.0);
                    }}
                    onBlur={(e) => {
                      let val = parseFloat(e.target.value);
                      if (isNaN(val)) return;
                      handleServicingChange('edit', 'servFeePct', val);
                      handleServicingChange('edit', 'servFeeAmt', 0.0);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Flat Service Fee:</th>
                <td>
                  <input
                    type='number'
                    name='zServFeeAmt'
                    value={String(servicingData?.servFeeAmt || '')}
                    placeholder='Service Fee'
                    onChange={(e) => {
                      let val = parseFloat(e.target.value);
                      if (isNaN(val)) return;
                      handleServicingChange('edit', 'servFeeAmt', val);
                      handleServicingChange('edit', 'servFeePct', 0.0);
                    }}
                    onBlur={(e) => {
                      let val = parseFloat(e.target.value);
                      if (isNaN(val)) return;
                      handleServicingChange('edit', 'servFeeAmt', val);
                      handleServicingChange('edit', 'servFeePct', 0.0);
                    }}
                  />
                </td>
              </tr>
              {loanData?.loanFha && (
                <tr>
                  <th>FHA Case Number: </th>
                  <td>{loanData?.agencyCaseNumber ? loanData?.agencyCaseNumber : ''}</td>
                </tr>
              )}
              {loanData?.loanVa && (
                <tr>
                  <th>VA Case Number: </th>
                  <td>{loanData?.agencyCaseNumber ? loanData?.agencyCaseNumber : ''}</td>
                </tr>
              )}
              {loanData?.loanUsda && (
                <tr>
                  <th>RHS/USDA Case Number: </th>
                  <td>{loanData?.agencyCaseNumber ? loanData?.agencyCaseNumber : ''}</td>
                </tr>
              )}
              <tr>
                <th>Tax Contract Number: </th>
                <td>{loanData?.contractNo ? loanData?.contractNo : ''}</td>
              </tr>
              <tr>
                <th>MERS Number: </th>
                <td>
                  <input
                    type='text'
                    maxLength={20}
                    value={loanData?.mersId ? loanData?.mersId : ''}
                    onChange={(e) => handleLoanChange('edit', 'mersId', e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <th>Flood Community: </th>
                <td>
                  <input
                    type='text'
                    name='floodCommunity'
                    value={propData?.floodCommunity || ''}
                    onChange={(e) => handlePropChange('edit', 'floodCommunity', e.target.value)}
                    placeholder='Flood Community'
                  />
                </td>
              </tr>
              <tr>
                <th>Flood Zone: </th>
                <td>
                  <select
                    value={propData?.propertyFloodZone ? propData?.propertyFloodZone : ''}
                    onChange={(e) => handlePropChange('edit', 'propertyFloodZone', e.target.value)}
                  >
                    <option value='' disabled>
                      -- Select an Option --
                    </option>
                    {floodZoneQid.map((opt, i) => (
                      <option key={i} value={opt.code}>
                        {opt.code} - {opt.description}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
              <tr>
                <th>Flood Panel: </th>
                <td>
                  <input
                    type='text'
                    value={propData?.floodPanel ? propData.floodPanel : ''}
                    maxLength={10}
                    onChange={(e) => handlePropChange('edit', 'floodPanel', e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <th>Flood Determination Date:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='V04'
                    value={loanEvents?.V04?.activeDate ? formatDateForInputWithOffset(loanEvents?.V04?.activeDate) : ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!value) {
                        e.target.value = null;
                        handleLoanEventChange('edit', 'V04.activeDate', '');
                        handleLoanEventChange('edit', 'V04.active', false);
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const ts = dateToEpoch(localDate);

                      handleLoanEventChange('edit', 'V04.activeDate', ts);
                      handleLoanEventChange('edit', 'V04.active', true);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Original Appraisal Date:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='appraised date'
                    value={propData?.appraisedDate ? formatDateForInputWithOffset(propData?.appraisedDate) : ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!value) {
                        e.target.value = null;
                        handlePropChange('edit', 'appraisedDate', '');
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const ts = dateToEpoch(localDate);

                      handlePropChange('edit', 'appraisedDate', ts);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Original Appraisal Value: </th>
                <td>
                  <NumericFormat
                    name='marketValue'
                    value={propData?.marketValue || null}
                    thousandSeparator={true}
                    prefix={'$'}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    allowNegative={false}
                    placeholder='Appraisal Value'
                    onValueChange={(e) => handlePropChange('edit', 'marketValue', e.value || 0)}
                  />
                </td>
              </tr>
              <tr>
                <th>Census Tract: </th>
                <td>{propData?.censusTract ? propData?.censusTract : 0}</td>
              </tr>
              <tr>
                <th>Mortgage Term:</th>
                <td>
                  <NumericFormat
                    value={loanData?.loanTerm ? loanData?.loanTerm : 0}
                    decimalScale={0}
                    disabled={!fullAccessSuperUser}
                    onValueChange={(values, sourceInfo) => {
                      handleLoanChange('edit', 'loanTerm', values.floatValue);
                      if (originalLoanData?.loanTerm == values.floatValue) {
                        handleLoanChange('cancel', 'depProtectLoanTerm');
                      } else {
                        handleLoanChange('edit', 'depProtectLoanTerm', false);
                      }
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Amortization Term:</th>
                <td>
                  <NumericFormat
                    value={loanData?.amortizationTerm ? loanData?.amortizationTerm : 0}
                    decimalScale={0}
                    disabled={!fullAccessSuperUser}
                    onValueChange={(values, sourceInfo) => {
                      handleLoanChange('edit', 'amortizationTerm', values.floatValue);
                      if (originalLoanData?.amortizationTerm == values.floatValue) {
                        handleLoanChange('cancel', 'depProtectAmortizationTerm');
                      } else {
                        handleLoanChange('edit', 'depProtectAmortizationTerm', false);
                      }
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Remaining Term: </th>
                <td>{lnptData?.remainingTerm ? lnptData?.remainingTerm : 0}</td>
              </tr>
            </tbody>
            <tr>
              <td colSpan={2} style={{ height: '20px' }}></td>
            </tr>
          </table>
        </div>
        <div className={Styles.rightColumn}>
          <table className={Styles.loanInfoTableSection}>
            <tbody>
              <tr>
                <th>Original Escrow Balance:</th>
                <td>
                  <NumericFormat
                    value={loanData?.escrowsDepositedAtClosing ? loanData?.escrowsDepositedAtClosing : ''}
                    decimalScale={2}
                    fixedDecimalScale
                    disabled={false}
                    prefix='$'
                    thousandSeparator
                    displayType='text'
                  />
                </td>
              </tr>
              <tr>
                <th>Closed on:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='SDT'
                    value={loanEvents?.SDT?.activeDate ? formatDateForInputWithOffset(loanEvents?.SDT?.activeDate) : ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!value) {
                        e.target.value = null;
                        handleLoanEventChange('edit', 'SDT.activeDate', '');
                        handleLoanEventChange('edit', 'SDT.active', false);
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const ts = dateToEpoch(localDate);

                      handleLoanEventChange('edit', 'SDT.activeDate', ts);
                      handleLoanEventChange('edit', 'SDT.active', true);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Funding Date:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='C06'
                    value={loanEvents?.C06?.activeDate ? formatDateForInputWithOffset(loanEvents?.C06?.activeDate) : ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!value) {
                        e.target.value = null;
                        handleLoanEventChange('edit', 'C06.activeDate', '');
                        handleLoanEventChange('edit', 'C06.active', false);
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const ts = dateToEpoch(localDate);

                      handleLoanEventChange('edit', 'C06.activeDate', ts);
                      handleLoanEventChange('edit', 'C06.active', true);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>First Payment Date:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='first payment date'
                    value={loanData?.firstPaymentDate ? formatDateForInputWithOffset(loanData?.firstPaymentDate) : ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!value) {
                        e.target.value = null;
                        handleLoanChange('edit', 'firstPaymentDate', '');
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const ts = dateToEpoch(localDate);

                      handleLoanChange('edit', 'firstPaymentDate', ts);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Purchase First Payment Date:</th>
                <td>
                  <input
                    type='date'
                    className={Styles.calicon}
                    name='purchase first payment date'
                    value={
                      servicingData?.startingPaymentDate
                        ? formatDateForInputWithOffset(servicingData?.startingPaymentDate)
                        : ''
                    }
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!value) {
                        e.target.value = null;
                        handleServicingChange('edit', 'startingPaymentDate', '');
                        return;
                      }

                      const localDate = convertDateInputTo8AMLocal(value);
                      const ts = dateToEpoch(localDate);

                      handleServicingChange('edit', 'startingPaymentDate', ts);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Purchase Principal Balance:</th>
                <td>
                  <NumericFormat
                    value={servicingData?.principalPurchased ? servicingData?.principalPurchased : ''}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                    disabled={false}
                    onValueChange={(values, sourceInfo) => {
                      handleServicingChange('edit', 'principalPurchased', values.floatValue);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Maturity Date:</th>
                <td>
                  {lnptData?.remainingTerm === 0 || !loanData?.firstPaymentDate
                    ? getTimestampAsDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime() / 1000)
                    : addMonthsToDate(loanData?.firstPaymentDate, loanData?.loanTerm - 1)}
                </td>
              </tr>
              <tr>
                <th>Calculated Maturity Date:</th>
                <td>
                  {typeof lnptData?.remainingTerm === 'number'
                    ? addMonthsToDate(loanData?.lpi, lnptData?.remainingTerm)
                    : ''}
                </td>
              </tr>
              <tr>
                <th>Total Discount: </th>
                <td>
                  {totalDiscount ? (
                    <NumericFormat
                      displayType='text'
                      value={totalDiscount}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <th>Borrower Discount: </th>
                <td>
                  {borrowerDiscount ? (
                    <NumericFormat
                      displayType='text'
                      value={borrowerDiscount}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <th>Upfront MIP:</th>
                <td>
                  <NumericFormat
                    value={loanData?.upfrontMIP ? loanData?.upfrontMIP : ''}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                    displayType='text'
                  />
                </td>
              </tr>
              <tr>
                <th>Upfront MIP Refund:</th>
                <td>
                  <NumericFormat
                    value={loanData?.MIPRefundAmount ? loanData?.MIPRefundAmount : ''}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                    onValueChange={(values, sourceInfo) => {
                      handleLoanChange('edit', 'MIPRefundAmount', values.floatValue);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Late Charge Percent:</th>
                <td>
                  <NumericFormat
                    value={loanData?.lteChrg ? loanData?.lteChrg : ''}
                    decimalScale={2}
                    fixedDecimalScale
                    suffix='%'
                    max={100}
                    thousandSeparator
                    onValueChange={(values, sourceInfo) => {
                      handleLoanChange('edit', 'lteChrg', values.floatValue);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Late Charge Type:</th>
                <td>
                  <select
                    name='late charge type'
                    value={lateChargeType}
                    disabled={!fullAccessSuperUser}
                    onChange={(e) => {
                      const newval = e.target.value == 'true' ? true : false;
                      handleLoanChange('edit', 'lateChrgType', newval);
                      if (originalLoanData.lateChrgType == newval) {
                        handleLoanChange('cancel', 'depProtectLateChrgType');
                      } else {
                        handleLoanChange('edit', 'depProtectLateChrgType', false);
                      }
                    }}
                  >
                    <option value='' disabled>
                      Select option
                    </option>
                    <option value='true'>PI</option>
                    <option value='false'>PITI</option>
                  </select>
                </td>
              </tr>
              <tr>
                <th>Late Charge</th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={calculatedLateCharge}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Late Charge Minimum</th>
                <td>
                  <NumericFormat
                    value={loanData?.lateChrgMin ? loanData?.lateChrgMin : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                    disabled={!fullAccessSuperUser}
                    onValueChange={(values, sourceInfo) => {
                      handleLoanChange('edit', 'lateChrgMin', values.floatValue);
                      if (originalLoanData.lateChrgMin == values.floatValue) {
                        handleLoanChange('cancel', 'depProtectLateChrgMin');
                      } else {
                        handleLoanChange('edit', 'depProtectLateChrgMin', false);
                      }
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Flat Late Charge:</th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={flatLateChargeValue}
                    decimalScale={2}
                    fixedDecimalScale
                    thousandSeparator
                    prefix='$'
                  />
                </td>
              </tr>
              <tr>
                <th>Old Loan Number:</th>
                <td>
                  <NumericFormat
                    value={loanData?.alternateLoanNumber ? loanData?.alternateLoanNumber : ''}
                    decimalScale={0}
                    maxLength={20}
                    disabled={false}
                    onValueChange={(values, sourceInfo) => {
                      handleLoanChange('edit', 'alternateLoanNumber', values.floatValue);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>Purpose:</th>
                <td>
                  <select
                    value={loanData?.loanPurpose ? loanData?.loanPurpose : ''}
                    onChange={(e) => handleLoanChange('edit', 'loanPurpose', e.target.value)}
                  >
                    <option value='' disabled>
                      -- Select an Option --
                    </option>
                    {loanPurposeQid.map((opt, i) => (
                      <option key={i} value={opt.code}>
                        {opt.code} - {opt.description}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            </tbody>
            <tr>
              <td colSpan={2} style={{ height: '20px' }}></td>
            </tr>
          </table>
        </div>
      </div>
    </EnvelopeWithBox>
  );
};

export default LoanDetails;
